import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TurnstileWidget from '../components/TurnstileWidget';
import api from '../services/api';
import { LangContext } from '../App';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

/* ── Canadian provinces ── */
const PROVINCES = [
  { code: 'AB', fr: 'Alberta', en: 'Alberta' },
  { code: 'BC', fr: 'Colombie-Britannique', en: 'British Columbia' },
  { code: 'MB', fr: 'Manitoba', en: 'Manitoba' },
  { code: 'NB', fr: 'Nouveau-Brunswick', en: 'New Brunswick' },
  { code: 'NL', fr: 'Terre-Neuve-et-Labrador', en: 'Newfoundland and Labrador' },
  { code: 'NS', fr: 'Nouvelle-Écosse', en: 'Nova Scotia' },
  { code: 'NT', fr: 'Territoires du Nord-Ouest', en: 'Northwest Territories' },
  { code: 'NU', fr: 'Nunavut', en: 'Nunavut' },
  { code: 'ON', fr: 'Ontario', en: 'Ontario' },
  { code: 'PE', fr: 'Île-du-Prince-Édouard', en: 'Prince Edward Island' },
  { code: 'QC', fr: 'Québec', en: 'Quebec' },
  { code: 'SK', fr: 'Saskatchewan', en: 'Saskatchewan' },
  { code: 'YT', fr: 'Yukon', en: 'Yukon' },
];

/* ── Roles ── */
const ROLES = [
  {
    id: 'hunter',
    icon: '🏹',
    accent: '#2a5a3a',
    fr: {
      title: 'Chasseur',
      desc: 'Accédez aux terres de chasse, enchérissez et réservez vos territoires.',
    },
    en: { title: 'Hunter', desc: 'Access hunting lands, bid and reserve your territories.' },
    verification: false,
  },
  {
    id: 'farmer',
    icon: '🌾',
    accent: '#4a6020',
    fr: {
      title: 'Agriculteur',
      desc: 'Publiez vos terres pour la chasse. Vérification foncière requise (≥ 4 ha).',
    },
    en: {
      title: 'Farmer',
      desc: 'List your land for hunting. Land registry verification required (≥ 4 ha).',
    },
    verification: true,
  },
  {
    id: 'landowner',
    icon: '🏡',
    accent: '#3a5848',
    fr: {
      title: 'Propriétaire',
      desc: 'Mettez en location vos territoires par enchère ou à prix fixe.',
    },
    en: { title: 'Landowner', desc: 'Rent out your territories by auction or fixed price.' },
    verification: false,
  },
  {
    id: 'guide',
    icon: '⛺',
    accent: '#2a4858',
    fr: {
      title: 'Guide',
      desc: 'Offrez vos services certifiés. 3 références vérifiées par province requises.',
    },
    en: {
      title: 'Guide',
      desc: 'Offer your certified services. 3 verified references per province required.',
    },
    verification: true,
  },
  {
    id: 'trapper',
    icon: '🐾',
    accent: '#583820',
    fr: { title: 'Trappeur', desc: 'Accédez aux zones de piégeage. Permis provincial requis.' },
    en: { title: 'Trapper', desc: 'Access trapping zones. Provincial license required.' },
    verification: true,
  },
  {
    id: 'arms_seller',
    icon: '🔧',
    accent: '#504020',
    fr: {
      title: 'Vendeur équipement',
      desc: 'Vendez armes et équipements certifiés PPA sur le Marché.',
    },
    en: {
      title: 'Equipment dealer',
      desc: 'Sell PPA-certified firearms and equipment on the Market.',
    },
    verification: true,
  },
];

/* ── Translations ── */
const T = {
  fr: {
    title: 'Créer votre compte',
    etape1: 'Choisissez votre rôle',
    etape2: 'Vos informations',
    verificationNote:
      'Ce rôle nécessite une vérification — vous serez notifié par courriel dans 24-48 h.',
    suivant: 'Continuer',
    retour: 'Retour',
    creerCompte: 'Créer mon compte',
    chargement: 'Création en cours…',
    name: 'Nom complet',
    email: 'Adresse courriel',
    mdp: 'Mot de passe',
    confirmerMdp: 'Confirmer le mot de passe',
    language: 'Langue préférée',
    langues: [
      { val: 'fr', label: 'Français' },
      { val: 'en', label: 'English' },
    ],
    // Agriculteur
    lot: 'Numéro de lot foncier',
    superficie: 'Superficie (hectares)',
    province: 'Province',
    // Guide
    provincesOp: "Provinces d'opération",
    refs: 'Références (3 requises)',
    refNom: 'Nom',
    refTel: 'Téléphone',
    refProv: 'Province',
    // Trappeur
    permis: 'Numéro de permis de piégeage',
    provincePiege: 'Province de piégeage',
    // Vendeur
    ppa: 'Numéro de permis PPA',
    // Consentement Loi 25
    consentement1:
      "J'accepte que mes informations personnelles soient utilisées exclusivement aux fins de la plateforme ChasseHunt, conformément à notre ",
    consentementLien: 'politique de confidentialité',
    consentement2: ' et à la Loi 25 (Québec).',
    errConsentement: 'Vous devez accepter la politique de confidentialité pour créer un compte.',
    // Errors
    errMdp: 'Les mots de passe ne correspondent pas.',
    errGenerique: 'Une erreur est survenue. Veuillez réessayer.',
    // Success & 2FA
    succes: 'Vérifiez votre courriel !',
    succesNote: "Un lien d'activation vous a été envoyé. Cliquez dessus pour activer votre compte.",
    succesDetail:
      'Si vous ne le recevez pas dans quelques minutes, vérifiez vos courriers indésirables.',
    configurer2FA: "Configurer l'authentification à deux facteurs (2FA)",
    configurer2FADesc:
      'Scannez ce QR code avec Google Authenticator, Authy ou une application TOTP similaire.',
    code2FA: 'Code à 6 chiffres',
    activer2FA: 'Activer le 2FA',
    ignorer2FA: "Ignorer pour l'instant",
    codes2FA: 'Codes de récupération (conservez-les en lieu sûr)',
    terminer: 'Terminer',
    retourAccueil: "Retour à l'accueil",
    dejaCompte: 'Déjà un compte ?',
    seConnecter: 'Se connecter',
    requis: 'Requis',
  },
  en: {
    title: 'Create your account',
    etape1: 'Choose your role',
    etape2: 'Your information',
    verificationNote:
      'This role requires verification — you will be notified by email within 24-48 h.',
    suivant: 'Continue',
    retour: 'Back',
    creerCompte: 'Create my account',
    chargement: 'Creating account…',
    name: 'Full name',
    email: 'Email address',
    mdp: 'Password',
    confirmerMdp: 'Confirm password',
    language: 'Preferred language',
    langues: [
      { val: 'fr', label: 'Français' },
      { val: 'en', label: 'English' },
    ],
    // Farmer
    lot: 'Land registry number',
    superficie: 'Area (hectares)',
    province: 'Province',
    // Guide
    provincesOp: 'Operating provinces',
    refs: 'References (3 required)',
    refNom: 'Name',
    refTel: 'Phone',
    refProv: 'Province',
    // Trapper
    permis: 'Trapping license number',
    provincePiege: 'Trapping province',
    // Dealer
    ppa: 'PPA permit number',
    // Consent Law 25
    consentement1:
      'I agree that my personal information will be used exclusively for the purposes of the ChasseHunt platform, in accordance with our ',
    consentementLien: 'privacy policy',
    consentement2: " and Quebec's Law 25.",
    errConsentement: 'You must accept the privacy policy to create an account.',
    // Errors
    errMdp: 'Passwords do not match.',
    errGenerique: 'An error occurred. Please try again.',
    // Success & 2FA
    succes: 'Check your email!',
    succesNote: 'An activation link has been sent to you. Click it to activate your account.',
    succesDetail: "If you don't receive it within a few minutes, check your spam folder.",
    configurer2FA: 'Set up two-factor authentication (2FA)',
    configurer2FADesc: 'Scan this QR code with Google Authenticator, Authy, or any TOTP app.',
    code2FA: '6-digit code',
    activer2FA: 'Enable 2FA',
    ignorer2FA: 'Skip for now',
    codes2FA: 'Recovery codes (keep them in a safe place)',
    terminer: 'Done',
    retourAccueil: 'Back to home',
    dejaCompte: 'Already have an account?',
    seConnecter: 'Sign in',
    requis: 'Required',
  },
};

/* ── Initial form state ── */
const formVide = {
  name: '',
  email: '',
  password_hash: '',
  confirmer: '',
  language: 'fr',
  // agriculteur
  land_lot_number: '',
  area_hectares: '',
  province: '',
  // guide
  operating_provinces: [],
  guide_references: [
    { name: '', phone: '', province: '' },
    { name: '', phone: '', province: '' },
    { name: '', phone: '', province: '' },
  ],
  // trappeur
  trapping_permit_number: '',
  trapping_province: '',
  // vendeur_armes
  ppa_number: '',
  // consentement
  consentement: false,
};

/* ── Shared input style ── */
const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '6px',
  color: 'var(--couleur-texte)',
  padding: '0.65rem 0.85rem',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  color: 'rgba(255,255,255,0.55)',
  marginBottom: '0.35rem',
  letterSpacing: '0.03em',
};

function Champ({ label, requis, children }) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {requis && <span style={{ color: 'var(--couleur-or)', marginLeft: '4px' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function SelectProvince({ name, value, onChange, t, lang, style = {} }) {
  return (
    <select
      name={name}
      value={value}
      onChange={onChange}
      style={{ ...inputStyle, ...style }}
      onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
    >
      <option value="">— {t.requis} —</option>
      {PROVINCES.map((p) => (
        <option key={p.code} value={p.code}>
          {p[lang]} ({p.code})
        </option>
      ))}
    </select>
  );
}

/* ── Role-specific extra fields ── */
function ChampsRole({ role, form, onChange, onRefChange, onProvOpToggle, t, lang }) {
  if (role === 'farmer') {
    return (
      <>
        <Champ label={t.lot} requis>
          <input
            name="land_lot_number"
            value={form.land_lot_number}
            onChange={onChange}
            style={inputStyle}
            placeholder="ex. 3 456 789"
            onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
            onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
          />
        </Champ>
        <Champ label={`${t.superficie} (≥ 4 ha)`} requis>
          <input
            name="area_hectares"
            type="number"
            min="4"
            step="0.1"
            value={form.area_hectares}
            onChange={onChange}
            style={inputStyle}
            placeholder="ex. 42"
            onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
            onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
          />
        </Champ>
        <Champ label={t.province} requis>
          <SelectProvince
            name="province"
            value={form.province}
            onChange={onChange}
            t={t}
            lang={lang}
          />
        </Champ>
      </>
    );
  }

  if (role === 'guide') {
    return (
      <>
        <Champ label={t.provincesOp} requis>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
            {PROVINCES.map((p) => {
              const checked = form.operating_provinces.includes(p.code);
              return (
                <label
                  key={p.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    background: checked ? 'rgba(201,162,39,0.18)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${checked ? 'rgba(201,162,39,0.5)' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '0.78rem',
                    color: checked ? 'var(--couleur-or)' : 'rgba(255,255,255,0.55)',
                    userSelect: 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onProvOpToggle(p.code)}
                    style={{ display: 'none' }}
                  />
                  {p.code}
                </label>
              );
            })}
          </div>
        </Champ>

        <div>
          <label style={labelStyle}>
            {t.refs} <span style={{ color: 'var(--couleur-or)' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {form.guide_references.map((ref, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '0.9rem',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '0.7rem',
                }}
              >
                <div>
                  <label style={{ ...labelStyle, fontSize: '0.72rem' }}>
                    {t.refNom} #{i + 1}
                  </label>
                  <input
                    value={ref.name}
                    onChange={(e) => onRefChange(i, 'name', e.target.value)}
                    style={{ ...inputStyle, fontSize: '0.82rem', padding: '0.5rem 0.7rem' }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                    placeholder={t.refNom}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: '0.72rem' }}>{t.refTel}</label>
                  <input
                    value={ref.phone}
                    onChange={(e) => onRefChange(i, 'phone', e.target.value)}
                    style={{ ...inputStyle, fontSize: '0.82rem', padding: '0.5rem 0.7rem' }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                    placeholder="418-555-0100"
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: '0.72rem' }}>{t.refProv}</label>
                  <SelectProvince
                    name={`ref_prov_${i}`}
                    value={ref.province}
                    onChange={(e) => onRefChange(i, 'province', e.target.value)}
                    t={t}
                    lang={lang}
                    style={{ fontSize: '0.82rem', padding: '0.5rem 0.7rem' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (role === 'trapper') {
    return (
      <>
        <Champ label={t.permis} requis>
          <input
            name="trapping_permit_number"
            value={form.trapping_permit_number}
            onChange={onChange}
            style={inputStyle}
            placeholder="ex. TRP-QC-2024-00123"
            onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
            onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
          />
        </Champ>
        <Champ label={t.provincePiege} requis>
          <SelectProvince
            name="trapping_province"
            value={form.trapping_province}
            onChange={onChange}
            t={t}
            lang={lang}
          />
        </Champ>
      </>
    );
  }

  if (role === 'arms_seller') {
    return (
      <Champ label={t.ppa} requis>
        <input
          name="ppa_number"
          value={form.ppa_number}
          onChange={onChange}
          style={inputStyle}
          placeholder="ex. PPA-2024-123456"
          onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
          onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
        />
      </Champ>
    );
  }

  return null;
}

/* ── Main component ── */
export default function Inscription() {
  const { lang } = useContext(LangContext);
  const navigate = useNavigate();
  const t = T[lang];

  const [etape, setEtape] = useState(1);
  const [roleChoisi, setRoleChoisi] = useState(null);
  const [form, setForm] = useState(formVide);
  const [erreurs, setErreurs] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [btnBloque, setBtnBloque] = useState(false);
  // Cloudflare Turnstile — token résolu côté widget, requis au submit
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [succes, setSucces] = useState(false);

  // 2FA setup state
  const [setupToken, setSetupToken] = useState(null);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [code2FA, setCode2FA] = useState('');
  const [chargement2FA, setChargement2FA] = useState(false);
  const [codesRecuperation, setCodesRecuperation] = useState(null);
  const [erreur2FA, setErreur2FA] = useState('');

  const roleInfo = ROLES.find((r) => r.id === roleChoisi);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleRefChange = (i, champ, val) => {
    const refs = form.guide_references.map((r, idx) => (idx === i ? { ...r, [champ]: val } : r));
    setForm({ ...form, guide_references: refs });
  };

  const handleProvOpToggle = (code) => {
    const ops = form.operating_provinces.includes(code)
      ? form.operating_provinces.filter((c) => c !== code)
      : [...form.operating_provinces, code];
    setForm({ ...form, operating_provinces: ops });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password_hash !== form.confirmer) {
      setErreurs([t.errMdp]);
      return;
    }
    if (!form.consentement) {
      setErreurs([t.errConsentement]);
      return;
    }
    setChargement(true);
    setBtnBloque(true);
    setErreurs([]);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password_hash: form.password_hash,
        user_type: roleChoisi,
        language: form.language,
        turnstile_token: turnstileToken,
      };
      if (roleChoisi === 'farmer') {
        payload.land_lot_number = form.land_lot_number;
        payload.area_hectares = parseFloat(form.area_hectares);
        payload.province = form.province;
      } else if (roleChoisi === 'guide') {
        payload.guide_references = form.guide_references;
        payload.operating_provinces = form.operating_provinces;
      } else if (roleChoisi === 'trapper') {
        payload.trapping_permit_number = form.trapping_permit_number;
        payload.trapping_province = form.trapping_province;
      } else if (roleChoisi === 'arms_seller') {
        payload.ppa_number = form.ppa_number;
      }
      const res = await api.post('/auth/register', payload);
      setSetupToken(res.data.setupToken);
      setRequires2FASetup(!!res.data.requires2FASetup);
      // Auto-déclencher la récupération du QR code
      if (res.data.setupToken) {
        try {
          const qrRes = await api.post(
            '/auth/2fa/setup',
            {},
            {
              headers: { Authorization: `Bearer ${res.data.setupToken}` },
            }
          );
          setQrCode(qrRes.data.qr_code);
        } catch {
          // QR code non bloquant — on peut continuer sans
        }
      }
      setSucces(true);
    } catch (err) {
      const data = err.response?.data;
      const msgs = data?.errors
        ? Array.isArray(data.errors)
          ? data.errors
          : [data.errors]
        : [data?.error || t.errGenerique];
      setErreurs(msgs);
    } finally {
      setChargement(false);
      // Reset le token Turnstile : un token n'est utilisable qu'une fois.
      // Le widget Cloudflare déclenchera automatiquement un nouveau callback
      // si l'utilisateur retente après échec.
      setTurnstileToken(null);
      setTimeout(() => setBtnBloque(false), 3000);
    }
  };

  /* ── Activer 2FA ── */
  const handleActiver2FA = async () => {
    if (!code2FA || code2FA.length !== 6) return;
    setChargement2FA(true);
    setErreur2FA('');
    try {
      const res = await api.post(
        '/auth/2fa/activer',
        { token: code2FA },
        { headers: { Authorization: `Bearer ${setupToken}` } }
      );
      setCodesRecuperation(res.data.codes_recuperation);
    } catch (err) {
      setErreur2FA(err.response?.data?.error || t.errGenerique);
    } finally {
      setChargement2FA(false);
    }
  };

  /* ── Success screen ── */
  if (succes) {
    // Codes de récupération affichés après activation 2FA
    if (codesRecuperation) {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'var(--couleur-fond)',
            color: 'var(--couleur-texte)',
          }}
        >
          <Header />
          <div style={{ maxWidth: '520px', margin: '4rem auto', padding: '2rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
              <h2
                style={{
                  fontFamily: 'Georgia, serif',
                  color: 'var(--couleur-or)',
                  fontSize: '1.4rem',
                  margin: 0,
                }}
              >
                2FA activé !
              </h2>
            </div>
            <p
              style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', fontSize: '0.85rem' }}
            >
              {t.codes2FA}
            </p>
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '1rem',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              {codesRecuperation.map((c, i) => (
                <code
                  key={i}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    color: 'var(--couleur-or)',
                    letterSpacing: '0.1em',
                  }}
                >
                  {c}
                </code>
              ))}
            </div>
            <button
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                background: 'var(--couleur-or)',
                color: 'var(--couleur-fond-carte)',
                border: 'none',
                padding: '0.8rem',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              {t.terminer}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--couleur-fond)',
          color: 'var(--couleur-texte)',
        }}
      >
        <Header />
        <div style={{ maxWidth: '520px', margin: '4rem auto', padding: '2rem' }}>
          {/* Email verification confirmation */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>📧</div>
            <h2
              style={{
                fontFamily: 'Georgia, serif',
                color: 'var(--couleur-or)',
                fontSize: '1.6rem',
                margin: '0 0 0.6rem',
              }}
            >
              {t.succes}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '0.5rem', lineHeight: 1.6 }}>
              {t.succesNote}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.83rem' }}>{t.succesDetail}</p>
          </div>

          {/* 2FA setup block */}
          {qrCode && (
            <div
              style={{
                background: 'rgba(201,162,39,0.06)',
                border: '1px solid rgba(201,162,39,0.25)',
                borderRadius: '10px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <h3
                style={{
                  fontFamily: 'Georgia, serif',
                  color: 'var(--couleur-or)',
                  fontSize: '1rem',
                  margin: '0 0 0.6rem',
                }}
              >
                🔐 {t.configurer2FA}
                {requires2FASetup && (
                  <span
                    style={{
                      marginLeft: '8px',
                      fontSize: '0.68rem',
                      background: 'rgba(200,50,50,0.2)',
                      border: '1px solid rgba(200,50,50,0.4)',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      color: 'var(--couleur-texte-erreur)',
                      verticalAlign: 'middle',
                    }}
                  >
                    {lang === 'fr' ? 'Obligatoire' : 'Required'}
                  </span>
                )}
              </h3>
              <p
                style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.82rem',
                  marginBottom: '1rem',
                }}
              >
                {t.configurer2FADesc}
              </p>
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <img
                  src={qrCode}
                  alt="QR Code 2FA"
                  style={{ width: '180px', height: '180px', borderRadius: '8px' }}
                />
              </div>
              <div
                style={{ display: 'flex', gap: '0.6rem', marginBottom: erreur2FA ? '0.5rem' : 0 }}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code2FA}
                  onChange={(e) => setCode2FA(e.target.value.replace(/\D/g, ''))}
                  placeholder={t.code2FA}
                  style={{
                    flex: 1,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px',
                    color: 'var(--couleur-texte)',
                    padding: '0.65rem 0.85rem',
                    fontSize: '1.1rem',
                    letterSpacing: '0.25em',
                    textAlign: 'center',
                  }}
                />
                <button
                  onClick={handleActiver2FA}
                  disabled={chargement2FA || code2FA.length !== 6}
                  style={{
                    background: code2FA.length === 6 ? 'var(--couleur-or)' : 'rgba(201,162,39,0.3)',
                    color: 'var(--couleur-fond-carte)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.65rem 1.2rem',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    cursor: code2FA.length === 6 ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {chargement2FA ? '…' : t.activer2FA}
                </button>
              </div>
              {erreur2FA && (
                <p
                  style={{
                    color: 'var(--couleur-texte-erreur)',
                    fontSize: '0.82rem',
                    margin: '0.5rem 0 0',
                  }}
                >
                  {erreur2FA}
                </p>
              )}
              {!requires2FASetup && (
                <button
                  onClick={() => navigate('/')}
                  style={{
                    marginTop: '0.8rem',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: 'rgba(255,255,255,0.45)',
                    borderRadius: '6px',
                    padding: '0.5rem 1rem',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  {t.ignorer2FA}
                </button>
              )}
            </div>
          )}

          {/* Back to home (only shown if 2FA not required or no QR loaded) */}
          {!requires2FASetup && (
            <button
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                background: qrCode ? 'transparent' : 'var(--couleur-or)',
                color: qrCode ? 'rgba(255,255,255,0.4)' : 'var(--couleur-fond-carte)',
                border: qrCode ? '1px solid rgba(255,255,255,0.15)' : 'none',
                padding: '0.8rem',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              {t.retourAccueil}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--couleur-fond)',
        color: 'var(--couleur-texte)',
      }}
    >
      <Header />

      <div style={{ maxWidth: '780px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>
        {/* Page title + steps indicator */}
        <div style={{ textAlign: 'center', marginBottom: '2.8rem' }}>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: '1.8rem',
              color: 'var(--couleur-texte)',
              margin: '0 0 1.5rem',
            }}
          >
            {t.title}
          </h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.8rem',
            }}
          >
            {[1, 2].map((n) => (
              <React.Fragment key={n}>
                <div
                  style={{
                    width: '2.2rem',
                    height: '2.2rem',
                    borderRadius: '50%',
                    background: etape >= n ? 'var(--couleur-or)' : 'rgba(255,255,255,0.08)',
                    color: etape >= n ? 'var(--couleur-fond-carte)' : 'rgba(255,255,255,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    border: etape >= n ? 'none' : '1px solid rgba(255,255,255,0.15)',
                    transition: 'all 0.2s',
                  }}
                >
                  {n}
                </div>
                {n < 2 && (
                  <div
                    style={{
                      width: '4rem',
                      height: '2px',
                      background: etape > 1 ? 'var(--couleur-or)' : 'rgba(255,255,255,0.12)',
                      borderRadius: '1px',
                      transition: 'background 0.3s',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', marginTop: '0.7rem' }}>
            {etape === 1 ? t.etape1 : t.etape2}
          </p>
        </div>

        {/* ── Étape 1 : Choix du rôle ── */}
        {etape === 1 && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1rem',
              }}
            >
              {ROLES.map((role) => {
                const info = role[lang];
                const selected = roleChoisi === role.id;
                return (
                  <button
                    key={role.id}
                    onClick={() => setRoleChoisi(role.id)}
                    style={{
                      background: selected ? `${role.accent}55` : 'rgba(255,255,255,0.03)',
                      border: `2px solid ${selected ? role.accent : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '10px',
                      padding: '1.4rem 1.2rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.18s',
                      position: 'relative',
                      outline: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) e.currentTarget.style.borderColor = `${role.accent}88`;
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    }}
                  >
                    {/* Selection indicator */}
                    {selected && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '1.2rem',
                          height: '1.2rem',
                          borderRadius: '50%',
                          background: 'var(--couleur-or)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.65rem',
                        }}
                      >
                        ✓
                      </div>
                    )}

                    <div style={{ fontSize: '2rem', marginBottom: '0.7rem' }}>{role.icon}</div>
                    <div
                      style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: selected ? 'var(--couleur-texte)' : 'rgba(255,255,255,0.8)',
                        marginBottom: '0.4rem',
                      }}
                    >
                      {info.title}
                    </div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: 'rgba(255,255,255,0.45)',
                        lineHeight: 1.5,
                      }}
                    >
                      {info.desc}
                    </div>
                    {role.verification && (
                      <div
                        style={{
                          marginTop: '0.8rem',
                          fontSize: '0.68rem',
                          color: 'var(--couleur-or)',
                          background: 'rgba(201,162,39,0.1)',
                          border: '1px solid rgba(201,162,39,0.25)',
                          borderRadius: '4px',
                          padding: '2px 7px',
                          display: 'inline-block',
                        }}
                      >
                        ✦ {lang === 'fr' ? 'Vérification requise' : 'Verification required'}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: '2.2rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  if (roleChoisi) setEtape(2);
                }}
                disabled={!roleChoisi}
                style={{
                  background: roleChoisi ? 'var(--couleur-or)' : 'rgba(255,255,255,0.1)',
                  color: roleChoisi ? 'var(--couleur-fond-carte)' : 'rgba(255,255,255,0.3)',
                  border: 'none',
                  padding: '0.8rem 2rem',
                  borderRadius: '6px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: roleChoisi ? 'pointer' : 'not-allowed',
                  transition: 'all 0.15s',
                }}
              >
                {t.suivant} →
              </button>
            </div>

            {/* Already have account */}
            <p
              style={{
                textAlign: 'center',
                marginTop: '1.5rem',
                color: 'rgba(255,255,255,0.38)',
                fontSize: '0.84rem',
              }}
            >
              {t.dejaCompte}{' '}
              <Link to="/login" style={{ color: 'var(--couleur-or)', textDecoration: 'none' }}>
                {t.seConnecter}
              </Link>
            </p>
          </div>
        )}

        {/* ── Étape 2 : Formulaire ── */}
        {etape === 2 && roleInfo && (
          <form onSubmit={handleSubmit} noValidate>
            {/* Role reminder badge */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem',
                background: `${roleInfo.accent}33`,
                border: `1px solid ${roleInfo.accent}66`,
                borderRadius: '8px',
                padding: '0.8rem 1.1rem',
                marginBottom: '2rem',
              }}
            >
              <span style={{ fontSize: '1.5rem' }}>{roleInfo.icon}</span>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--couleur-texte)', fontSize: '0.9rem' }}>
                  {roleInfo[lang].title}
                </div>
                {roleInfo.verification && (
                  <div
                    style={{ fontSize: '0.76rem', color: 'rgba(201,162,39,0.8)', marginTop: '2px' }}
                  >
                    {t.verificationNote}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              {/* Common fields */}
              <Champ label={t.name} requis>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="Marie Tremblay"
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                />
              </Champ>

              <Champ label={t.email} requis>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="marie@exemple.ca"
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                />
              </Champ>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Champ label={t.mdp} requis>
                  <input
                    name="password_hash"
                    type="password"
                    value={form.password_hash}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder="••••••••"
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                  />
                </Champ>
                <Champ label={t.confirmerMdp} requis>
                  <input
                    name="confirmer"
                    type="password"
                    value={form.confirmer}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder="••••••••"
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                  />
                </Champ>
              </div>

              <Champ label={t.language}>
                <select
                  name="language"
                  value={form.language}
                  onChange={handleChange}
                  style={{ ...inputStyle, maxWidth: '200px' }}
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.6)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.18)')}
                >
                  {t.langues.map((l) => (
                    <option key={l.val} value={l.val}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Champ>

              {/* Role-specific fields */}
              <ChampsRole
                role={roleChoisi}
                form={form}
                onChange={handleChange}
                onRefChange={handleRefChange}
                onProvOpToggle={handleProvOpToggle}
                t={t}
                lang={lang}
              />

              {/* Errors */}
              {erreurs.length > 0 && (
                <div
                  style={{
                    background: 'rgba(200,50,50,0.12)',
                    border: '1px solid rgba(200,50,50,0.35)',
                    borderRadius: '6px',
                    padding: '0.9rem 1.1rem',
                  }}
                >
                  {erreurs.map((e, i) => (
                    <p
                      key={i}
                      style={{
                        color: 'var(--couleur-texte-erreur)',
                        margin: i === 0 ? 0 : '0.35rem 0 0',
                        fontSize: '0.84rem',
                      }}
                    >
                      {e}
                    </p>
                  ))}
                </div>
              )}

              {/* Consentement Loi 25 */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.9rem 1rem',
                  background: 'rgba(201,162,39,0.05)',
                  border: `1px solid ${form.consentement ? 'rgba(201,162,39,0.4)' : 'rgba(255,255,255,0.12)'}`,
                  borderRadius: '8px',
                  transition: 'border-color 0.2s',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.consentement}
                  onChange={(e) => setForm({ ...form, consentement: e.target.checked })}
                  style={{
                    marginTop: '2px',
                    width: '16px',
                    height: '16px',
                    accentColor: 'var(--couleur-or)',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}
                >
                  {t.consentement1}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--couleur-or)', textDecoration: 'underline' }}
                  >
                    {t.consentementLien}
                  </Link>
                  {t.consentement2}
                  <span style={{ color: 'var(--couleur-or)', marginLeft: '4px' }}>*</span>
                </span>
              </label>

              {/* Cloudflare Turnstile — protection anti-bots, requise au submit */}
              <TurnstileWidget
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />

              {/* Actions */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '0.5rem',
                  gap: '1rem',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setEtape(1);
                    setErreurs([]);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.6)',
                    padding: '0.7rem 1.4rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.88rem',
                  }}
                >
                  ← {t.retour}
                </button>
                <button
                  type="submit"
                  disabled={chargement || btnBloque || !turnstileToken}
                  style={{
                    background:
                      chargement || btnBloque || !turnstileToken
                        ? 'rgba(201,162,39,0.5)'
                        : 'var(--couleur-or)',
                    color: 'var(--couleur-fond-carte)',
                    border: 'none',
                    padding: '0.75rem 2rem',
                    borderRadius: '6px',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: chargement || btnBloque || !turnstileToken ? 'not-allowed' : 'pointer',
                    transition: 'filter 0.15s',
                  }}
                >
                  {chargement ? t.chargement : t.creerCompte}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
